import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Dropdown, Menu, Popover, Tooltip, Modal, MenuProps } from 'antd'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import SVG from 'react-inlinesvg'
import FTIcon from 'components/FTIcon'

import DOMPurify from 'dompurify'
import EditResponseComment from '../EditResponseComment'
import AttendeeList from '../AttendeeList'
import Avatar from '../Avatar'
import * as calendarService from '../../modules/CalendarService'
import * as dateUtils from '../../utils/DateUtils'
import { doMeetingResponse } from '../../modules/EventEditProcessor'
import { confirmDelete } from 'utils/ui'
import { isOrgSharedAccountEmail } from 'utils/orgSharedAccount'
import NoteDetail from '../NoteDetail'
import { rruleDescription } from '../../utils/RRuleDesc'
import closeIcon from 'assets/images/toolbar-close.svg'
import editIcon from 'assets/images/toolbar-edit.svg'
import deleteIcon from 'assets/images/toolbar-delete.svg'
import duplicateIcon from 'assets/images/toolbar-duplicate-24.svg'
import locationIcon from 'assets/images/calendar/location-icon.svg'
import folderIcon from 'assets/images/calendar/calendar-folder-icon.svg'
import busyIcon from 'assets/images/calendar/busy-status-icon.svg'
import noteIcon from 'assets/images/calendar/note-icon.svg'
import googleMeetingIcon from 'assets/images/calendar/google-meeting-icon.svg'
import attendeeIcon from 'assets/images/calendar/property-attendees.svg'
import reminderIcon from 'assets/images/calendar/reminder-icon.svg'
import meetingRsvpIcon from 'assets/images/calendar/property-rsvp.svg'
import attachmentIcon from 'assets/images/calendar/attachment-20.svg'
import mapsIcon from 'assets/images/icon-map-20.svg'
import videoIcon from 'assets/images/icon-video-24.svg'

import styles from './style.module.scss'

type DeleteType = 'all' | 'future' | 'this'
type EditType = 'all' | 'future' | 'this' | null
type MeetingStatus = 'ACCEPTED' | 'DECLINED' | 'TENTATIVE'

interface AttendeeParams {
  CN?: string
}

interface Attendee {
  val: string
  params: AttendeeParams
}

interface Author {
  val: string
  params?: AttendeeParams
}

interface Attachment {
  url: string
  filename: string
  mimeType: string
}

interface EventException {
  recurrenceid?: Date
}

interface CalendarEvent {
  subject: string
  startDate: Date
  endDate: Date
  allday: boolean
  location?: string
  description?: string
  color: string
  serverId: string
  isPrivate?: boolean
  isRecurring?: boolean
  hasAttachment?: boolean
  isMeeting?: boolean
  isMyEvent?: boolean
  isCanceled?: boolean
  isDeclined?: boolean
  isContainMe?: boolean
  isBusy?: boolean
  author?: Author
  attendees: Attendee[]
  attachments?: Attachment[]
  alarmsStrings: string[]
  myMeetingResponse?: MeetingStatus
  event: {
    start: Date
    rrule?: any
  }
  exception?: EventException
}

interface Calendar {
  displayName: string
  owner: string
  isReadOnly: boolean
}

interface EventDetailProps {
  event: CalendarEvent | null
  onEdit: (editType: EditType) => void
  onDuplicate: (event: CalendarEvent) => void
  onClose: () => void
  onDidRender: (element: HTMLDivElement) => void
}

const EventDetail = ({ event, onEdit, onDuplicate, onClose, onDidRender }: EventDetailProps) => {
  if (!event) return null
  console.log('detail event : %o', event)

  const { t } = useTranslation()

  const me = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (me.current) {
      onDidRender(me.current)
    }
  }, [onDidRender])

  const handleEventDelete = (event: CalendarEvent, deleteType: DeleteType = 'all') => {
    let content = null
    let title = null
    let okText = null
    let cancelText = null

    let contentText = null

    if (event.isRecurring) {
      switch (deleteType) {
        case 'future':
          contentText = t('calendar.result.deleteFutureContent')
          break
        case 'this':
          contentText = t('calendar.result.deleteThisContent')
          break
        case 'all':
          contentText = t('calendar.result.deleteAllContent')
          break
      }
    } else {
      contentText = t('calendar.result.deleteContent')
    }

    if (event.isMeeting && event.isMyEvent) {
      title = t('calendar.result.cancelMeetingTitle')
      content = t('calendar.result.cancelMeetingContent')
      okText = t('common.button.ok')
      cancelText = t('common.button.cancel')
    } else {
      title = t('calendar.result.deleteTitle')
      content = contentText
      okText = t('common.button.ok')
      cancelText = t('common.button.cancel')
    }
    confirmDelete({
      title: title,
      content: content,
      okText: okText,
      cancelText: cancelText,
      onOk: () => {
        console.log('OK')

        let deleteEventType: 0 | 1 | 2 = calendarService.EventDeleteType.ALL
        if (deleteType === 'future') {
          deleteEventType = calendarService.EventDeleteType.AFTER_ALL
        } else if (deleteType === 'this') {
          deleteEventType = calendarService.EventDeleteType.THIS_ONLY
        }
        calendarService.deleteEvent(event as any, deleteEventType)

        onClose()
      },
      onCancel: () => {
        console.log('Cancel')
      },
    })
  }

  const parentCalendar: Calendar | null = useMemo(
    () => calendarService.findCalendarByEventServerId(event.serverId) as any,
    [event.serverId],
  )

  const handleDuplicateEvent = () => {
    onDuplicate(event)
  }

  const renderToolbar = () => {
    const positionDate = event.exception?.recurrenceid || event.startDate
    const recurStart = event.event.start

    let hideFuture = false
    if (
      positionDate.getFullYear() === recurStart.getFullYear() &&
      positionDate.getMonth() === recurStart.getMonth() &&
      positionDate.getDate() === recurStart.getDate()
    ) {
      hideFuture = true
    }

    const deleteMenuItems: MenuProps['items'] = [
      {
        key: 'this',
        label: t('calendar.edit.thisEvent'),
        onClick: () => handleEventDelete(event, 'this'),
      },
      !hideFuture && {
        key: 'future',
        label: t('calendar.edit.thisAndAllFutureEvents'),
        onClick: () => handleEventDelete(event, 'future'),
      },
      {
        key: 'all',
        label: t('calendar.edit.allEventsInTheSeries'),
        onClick: () => handleEventDelete(event, 'all'),
      },
    ].filter(Boolean)

    const editMenuItems: MenuProps['items'] = [
      {
        key: 'this',
        label: t('calendar.edit.thisEvent'),
        onClick: () => onEdit('this'),
      },
      !hideFuture && {
        key: 'future',
        label: t('calendar.edit.thisAndAllFutureEvents'),
        onClick: () => onEdit('future'),
      },
      {
        key: 'all',
        label: t('calendar.edit.allEventsInTheSeries'),
        onClick: () => onEdit('all'),
      },
    ].filter(Boolean)

    const deleteMenu = <Menu items={deleteMenuItems} className={classNames(styles.contextMenu)} />
    const editMenu = <Menu items={editMenuItems} className={classNames(styles.contextMenu)} />

    return (
      <div className={styles.toolbar}>
        <div className={styles.leftIcons}>
          {event.author && (
            <Tooltip title={event.author.params?.CN || event.author.val}>
              <div className={styles.authorIcon}>
                <Avatar
                  name={event.author.params?.CN || event.author.val}
                  email={event.author.val}
                />
              </div>
            </Tooltip>
          )}
        </div>
        <div className={styles.toolbarButtons}>
          <Tooltip title={t('signature.menu.duplicate')}>
            <button className={styles.duplicateButton} onClick={handleDuplicateEvent}>
              <img src={duplicateIcon} alt="Duplicate" />
            </button>
          </Tooltip>

          {parentCalendar && !parentCalendar.isReadOnly && (
            <Tooltip title={t('common.button.edit')}>
              {event.isRecurring ? (
                <Dropdown
                  popupRender={() => editMenu}
                  trigger={['click']}
                  overlayClassName={classNames('contextMenu')}
                >
                  <button className={styles.editButton}>
                    <img src={editIcon} alt="Edit" />
                  </button>
                </Dropdown>
              ) : (
                <button className={styles.editButton} onClick={() => onEdit(null)}>
                  <img src={editIcon} alt="Edit" />
                </button>
              )}
            </Tooltip>
          )}

          {parentCalendar && !parentCalendar.isReadOnly && (
            <Tooltip title={t('common.button.delete')}>
              {event.isRecurring ? (
                <Dropdown
                  popupRender={() => deleteMenu}
                  trigger={['click']}
                  overlayClassName={classNames('contextMenu')}
                >
                  <button className={styles.deleteButton}>
                    <img src={deleteIcon} alt="Delete" />
                  </button>
                </Dropdown>
              ) : (
                <button className={styles.deleteButton} onClick={() => handleEventDelete(event)}>
                  <img src={deleteIcon} alt="Delete" />
                </button>
              )}
            </Tooltip>
          )}

          <Tooltip title={t('common.button.close')}>
            <button className={styles.closeButton} onClick={onClose}>
              <img src={closeIcon} alt="Close" />
            </button>
          </Tooltip>
        </div>
      </div>
    )
  }

  const renderTitle = () => {
    let subject = event.subject
    if (subject == null || subject.length === 0) {
      subject = t('editor.value.noSubject')
    }

    return (
      <div className={styles.header}>
        <div className={styles.colorDot} style={{ backgroundColor: event.color }} />
        <div className={styles.title}>
          <div>{subject}</div>
        </div>
      </div>
    )
  }

  const renderTime = () => {
    let firstLine = ''
    let secondLine: string | null = null
    if (
      !event.allday &&
      dateUtils.isSameDay(event.startDate, new Date(event.endDate.getTime() - 1000))
    ) {
      firstLine = dateUtils.localizedDateString_ddddMMMMDYYYY(event.startDate)
      secondLine =
        dateUtils.localizedTimeString(event.startDate) +
        ' → ' +
        dateUtils.localizedTimeString(event.endDate)
    } else if (event.allday) {
      if (dateUtils.isSameDay(event.startDate, new Date(event.endDate.getTime() - 1000))) {
        firstLine =
          dateUtils.localizedDateString_ddddMMMMDYYYY(event.startDate) +
          ' (' +
          t('calendar.edit.allDay') +
          ')'
      } else {
        firstLine = dateUtils.localizedDateString_ddddMMMMDYYYY(event.startDate) + ' →'
        if (event.startDate.valueOf() !== event.endDate.valueOf()) {
          secondLine = dateUtils.localizedDateString_ddddMMMMDYYYY(
            new Date(event.endDate.getTime() - 1000),
          )
        } else {
          secondLine = dateUtils.localizedDateString_ddddMMMMDYYYY(event.endDate)
        }
      }
    } else {
      //multi day event.
      firstLine =
        dateUtils.localizedDateString_ddddMMMMDYYYY(event.startDate) +
        ' ' +
        dateUtils.localizedTimeString(event.startDate) +
        ' →'
      secondLine =
        dateUtils.localizedDateString_ddddMMMMDYYYY(event.endDate) +
        ' ' +
        dateUtils.localizedTimeString(event.endDate)
    }

    const rruleStr = event.event.rrule ? rruleDescription(event.event.rrule) : null

    return (
      <div className={styles.time}>
        <p>
          {firstLine}
          <br />
          {secondLine}
          {rruleStr && (
            <>
              <br />
              {rruleStr}
            </>
          )}
        </p>
      </div>
    )
  }

  const renderLocation = () => {
    if (!event.location) return null

    const showVideoIcon =
      event.location.includes('meet.google.com') || event.location.includes('.zoom.us')
    let location = event.location

    if (showVideoIcon && !location.startsWith('http')) {
      location = 'https://' + location
    }

    let l: URL | null = null
    try {
      l = new URL(location)
    } catch {
      console.log('create url failed')
    }

    return (
      <div className={styles.section}>
        <div className={styles.location}>
          <img src={locationIcon} alt="Location" className={styles.locationIcon} />
          {l ? (
            <div className={styles.locationText}>
              <a href={l.href} target="_blank" rel="noopener noreferrer">
                {event.location}
              </a>
              {showVideoIcon && (
                <a href={l.href} target="_blank" rel="noopener noreferrer">
                  <img src={videoIcon} alt="Link" />
                </a>
              )}
            </div>
          ) : (
            <div className={styles.locationText}>
              <span>{location}</span>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={mapsIcon} alt="Google Maps" />
              </a>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderGoogleMeet = () => {
    if (!event.description || !event.description.includes('meet.google.com')) return null
    const meetUrl = event.description.match(/meet\.google\.com\/[a-z-]+/)?.[0]
    if (!meetUrl) return null

    return (
      <div className={styles.section}>
        <div className={styles.googleMeet}>
          <img src={googleMeetingIcon} alt="Google Meet" />
          <div>
            <a href={`https://${meetUrl}`} target="_blank" rel="noopener noreferrer">
              {t('calendar.result.joinGoogleMeet')}
            </a>
            <div style={{ marginTop: '2px' }}>{meetUrl}</div>
          </div>
        </div>
      </div>
    )
  }

  const [attendeesToShow, setAttendeesToShow] = useState<Attendee[]>([])
  const [showMoreAttendees, setShowMoreAttendees] = useState(false)
  const maxAttendees = 8

  useEffect(() => {
    if (!event.attendees.length) return

    let attendeesToDisplay: Attendee[] = []
    if (event.attendees.length > maxAttendees) {
      attendeesToDisplay = event.attendees.slice(0, maxAttendees - 1)
      setShowMoreAttendees(true)
    } else {
      attendeesToDisplay = event.attendees
      setShowMoreAttendees(false)
    }
    setAttendeesToShow(attendeesToDisplay)
  }, [event.attendees])

  const [showAttendeeList, setShowAttendeeList] = useState(false)

  const handleAttendeeListClose = () => {
    setShowAttendeeList(false)
  }

  const renderAttendees = () => {
    if (event.attendees.length === 0) return null

    return (
      <div className={styles.section}>
        <div
          className={styles.attendees}
          onClick={() => setShowAttendeeList(true)}
          style={{ cursor: 'pointer' }}
        >
          <img src={attendeeIcon} alt="Attendees" />
          <div className={styles.attendeesList}>
            {attendeesToShow.map((attendee) => (
              <div key={attendee.val} className={styles.avatar}>
                <Avatar name={attendee.params.CN || attendee.val} email={attendee.val} />
                <div className={styles.tooltip}>
                  {attendee.params.CN ? `${attendee.params.CN}\n` : ''}
                  {attendee.val}
                </div>
              </div>
            ))}
            {showMoreAttendees && (
              <div className={styles.more}>+{event.attendees.length - (maxAttendees - 1)}</div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderAttachments = () => {
    if (!event.attachments || event.attachments.length === 0) return null

    return (
      <div className={styles.section}>
        <div className={styles.attachments}>
          <img src={attachmentIcon} alt="Attachments" />
          <div className={styles.attachmentList}>
            {event.attachments.map((attachment, index) => (
              <div
                key={index}
                className={styles.attachmentCell}
                onClick={() => calendarService.fetchAttachment(attachment.url, attachment.filename)}
              >
                <FTIcon fileName={attachment.filename} fileType={attachment.mimeType} size={24} />
                <div className={styles.attachmentName}>{attachment.filename}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const handleViewMore = () => {
    setShowNoteDetail(true)
  }

  const [showNoteDetail, setShowNoteDetail] = useState(false)
  const noteRef = useRef<HTMLDivElement>(null)
  const [isViewMore, setIsViewMore] = useState(false)

  useEffect(() => {
    const nRef = noteRef.current
    if (nRef) {
      const nRefHeight = nRef.clientHeight
      const nRefScrollHeight = nRef.scrollHeight
      if (nRefHeight < nRefScrollHeight) {
        setIsViewMore(true)
      }
    }
  }, [noteRef])

  const urlify = (text: string): string => {
    if (!text) return ''
    const urlRegex = /(https?:\/\/[^\s]+)/g
    return text.replace(urlRegex, function (url) {
      return '<a href="' + url + '" target="_blank">' + url + '</a>'
    })
  }
  const linkifyNote = urlify(event.description || '')

  const renderNote = () => {
    if (!event.description) return null
    return (
      <div className={styles.section}>
        <div className={styles.note}>
          <img src={noteIcon} alt="note" className={styles.noteIcon} />
          <div
            className={`${styles.textContent} ${isViewMore ? styles.viewMore : ''}`}
            ref={noteRef}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<p>${linkifyNote}</p>`) }}
          />
        </div>
        {isViewMore && (
          <button className={styles.viewMoreButton} onClick={handleViewMore}>
            {t('calendar.detail.viewMore')}
          </button>
        )}
      </div>
    )
  }

  console.log('event.alarmsStrings', event.alarmsStrings)

  const renderReminders = () => {
    if (event.alarmsStrings.length === 0) return null
    return (
      <div className={styles.section}>
        <div className={styles.reminders}>
          <img src={reminderIcon} alt="Reminders" />
          <div>
            {event.alarmsStrings.map((alarm) => (
              <span key={alarm}>{alarm}</span>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const renderFreeBusy = () => {
    return (
      <div className={styles.section}>
        <div className={styles.busyStatus}>
          <img src={busyIcon} alt="Busy" className={styles.busyStatusIcon} />
          <span>{event.isBusy ? t('calendar.edit.busy') : t('calendar.edit.free')}</span>
        </div>
      </div>
    )
  }

  const renderCalendar = () => {
    if (!parentCalendar) return null

    const isOrgShared = isOrgSharedAccountEmail(parentCalendar.owner)

    return (
      <div className={styles.section}>
        <div className={styles.folder}>
          <img src={folderIcon} alt="Calendar" className={styles.folderIcon} />
          <div>
            <span>{parentCalendar.displayName}</span>
            {!isOrgShared && (
              <>
                <br />
                <span>{parentCalendar.owner}</span>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  const [selectedStatus, setSelectedStatus] = useState<MeetingStatus | null>(null)
  const [selectedType, setSelectedType] = useState<'this' | 'all' | null>(null)
  const [changeMeetingResponse, setChangeMeetingResponse] = useState(false)
  const [showEditResponse, setShowEditResponse] = useState(false)

  const handleChangeMeetingResponse = (
    status: MeetingStatus,
    type: 'all' | 'this' = 'all',
    sendOption: 'now' | 'edit' = 'now',
  ) => {
    if (sendOption === 'edit') {
      setShowEditResponse(true)
    } else {
      doMeetingResponse(event as any, status, type, null, onClose)

      setSelectedStatus(null)
      setSelectedType(null)
      setChangeMeetingResponse(false)
    }
  }

  const handleEditResponseSubmit = (comment: string, status: MeetingStatus) => {
    doMeetingResponse(event as any, status, selectedType || 'all', comment, onClose)
    handleEditResponseClose()
    setChangeMeetingResponse(false)
  }

  const handleEditResponseClose = () => {
    setShowEditResponse(false)
    setSelectedStatus(null)
    setSelectedType(null)
  }

  const renderSendOptions = (status: MeetingStatus, type: 'this' | 'all') => {
    const items: MenuProps['items'] = [
      {
        key: 'edit',
        label: t('calendar.edit.editBeforeSend'),
        onClick: () => handleChangeMeetingResponse(status, type, 'edit'),
      },
      {
        key: 'send',
        label: t('calendar.edit.sendNow'),
        onClick: () => handleChangeMeetingResponse(status, type, 'now'),
      },
    ]
    return <Menu items={items} />
  }

  const renderRecurringOptions = (_status: MeetingStatus) => {
    const items: MenuProps['items'] = [
      {
        key: 'this',
        label: (
          <div
            style={{
              width: '100%',
              cursor: 'pointer',
            }}
            onClick={() => setSelectedType('this')}
          >
            {t('calendar.edit.thisEvent')}
          </div>
        ),
      },
      {
        key: 'all',
        label: (
          <div
            style={{
              cursor: 'pointer',
            }}
            onClick={() => setSelectedType('all')}
          >
            {t('calendar.edit.allEventsInTheSeries')}
          </div>
        ),
      },
    ]
    return <Menu items={items} selectable={false} />
  }

  const changeMeetingResponseState = () => {
    setChangeMeetingResponse(true)
    setTimeout(() => {
      if (me.current) {
        onDidRender(me.current)
      }
    }, 200)
  }

  const renderMeetingResponse = () => {
    if (!event.isMeeting || event.isMyEvent || event.isCanceled || !event.isContainMe) return null

    const showResponseButton = !event.myMeetingResponse || changeMeetingResponse
    let statusString = ''
    if (event.myMeetingResponse === 'ACCEPTED') {
      statusString = t('meeting.status.accepted')
    } else if (event.myMeetingResponse === 'DECLINED') {
      statusString = t('meeting.status.declined')
    } else if (event.myMeetingResponse === 'TENTATIVE') {
      statusString = t('meeting.status.tentative')
    }

    const handleResponseClick = (status: MeetingStatus) => {
      setSelectedStatus(status)
      if (!event.isRecurring) {
        setSelectedType('all')
      }
    }

    const acceptBtn = useRef<HTMLButtonElement>(null)
    const declineBtn = useRef<HTMLButtonElement>(null)
    const tentativeBtn = useRef<HTMLButtonElement>(null)

    return (
      <div className={styles.section}>
        <div className={styles.meetingResponse}>
          <SVG src={meetingRsvpIcon} title="Meeting RSVP" />
          <div style={{ marginLeft: '4px' }}>
            <span>{t('calendar.result.RSVP')}</span>
            {showResponseButton && (
              <div className={styles.responseButtons}>
                <button
                  ref={acceptBtn}
                  className={styles.accept}
                  onClick={() => handleResponseClick('ACCEPTED')}
                >
                  {t('common.button.accept')}
                </button>
                <button
                  ref={declineBtn}
                  className={styles.decline}
                  onClick={() => handleResponseClick('DECLINED')}
                >
                  {t('common.button.decline')}
                </button>
                <button
                  ref={tentativeBtn}
                  className={styles.tentative}
                  onClick={() => handleResponseClick('TENTATIVE')}
                >
                  {t('common.button.tentative')}
                </button>
                {selectedStatus && (
                  <div className={styles.dropdownContainer}>
                    {event.isRecurring ? (
                      selectedType ? (
                        <Popover
                          content={renderSendOptions(selectedStatus, selectedType)}
                          trigger="click"
                          open={true}
                          getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
                          styles={{
                            body: { padding: '4px 0' },
                          }}
                          onOpenChange={(open) => {
                            if (!open && !showEditResponse) {
                              setSelectedStatus(null)
                              setSelectedType(null)
                            }
                          }}
                        >
                          <div
                            style={{
                              height: 0,
                              position: 'absolute',
                              top: '-40px',
                              left:
                                selectedStatus === 'ACCEPTED' && acceptBtn.current
                                  ? acceptBtn.current.offsetLeft + acceptBtn.current.offsetWidth / 2
                                  : selectedStatus === 'DECLINED' && declineBtn.current
                                    ? declineBtn.current.offsetLeft +
                                      declineBtn.current.offsetWidth / 2
                                    : tentativeBtn.current
                                      ? tentativeBtn.current.offsetLeft +
                                        tentativeBtn.current.offsetWidth / 2
                                      : 0,
                            }}
                          />
                        </Popover>
                      ) : (
                        <Popover
                          content={renderRecurringOptions(selectedStatus)}
                          trigger="click"
                          open={true}
                          getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
                          styles={{
                            body: { padding: '4px 0' },
                          }}
                          onOpenChange={(open) => {
                            if (!open) {
                              setSelectedStatus(null)
                              setSelectedType(null)
                            }
                          }}
                        >
                          <div
                            style={{
                              height: 0,
                              position: 'absolute',
                              top: '-40px',
                              left:
                                selectedStatus === 'ACCEPTED' && acceptBtn.current
                                  ? acceptBtn.current.offsetLeft + acceptBtn.current.offsetWidth / 2
                                  : selectedStatus === 'DECLINED' && declineBtn.current
                                    ? declineBtn.current.offsetLeft +
                                      declineBtn.current.offsetWidth / 2
                                    : tentativeBtn.current
                                      ? tentativeBtn.current.offsetLeft +
                                        tentativeBtn.current.offsetWidth / 2
                                      : 0,
                            }}
                          />
                        </Popover>
                      )
                    ) : (
                      <Popover
                        content={renderSendOptions(selectedStatus, 'all')}
                        trigger="click"
                        open={true}
                        getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
                        styles={{
                          body: { padding: '4px 0' },
                        }}
                        onOpenChange={(open) => {
                          if (!open && !showEditResponse) {
                            setSelectedStatus(null)
                            setSelectedType(null)
                          }
                        }}
                      >
                        <div
                          style={{
                            height: 0,
                            position: 'absolute',
                            top: '-40px',
                            left:
                              selectedStatus === 'ACCEPTED' && acceptBtn.current
                                ? acceptBtn.current.offsetLeft + acceptBtn.current.offsetWidth / 2
                                : selectedStatus === 'DECLINED' && declineBtn.current
                                  ? declineBtn.current.offsetLeft +
                                    declineBtn.current.offsetWidth / 2
                                  : tentativeBtn.current
                                    ? tentativeBtn.current.offsetLeft +
                                      tentativeBtn.current.offsetWidth / 2
                                    : 0,
                          }}
                        />
                      </Popover>
                    )}
                  </div>
                )}
              </div>
            )}
            {!showResponseButton && (
              <div className={styles.responseStatus}>
                <span>{statusString}</span>
                <button className={styles.changeResponse} onClick={changeMeetingResponseState}>
                  {t('common.button.change')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.eventDetail} ref={me}>
      {renderToolbar()}
      {renderTitle()}
      {renderTime()}
      {renderLocation()}
      {renderGoogleMeet()}
      {renderAttendees()}
      {renderAttachments()}
      {renderNote()}
      {renderReminders()}
      {renderFreeBusy()}
      {renderCalendar()}
      {renderMeetingResponse()}
      <Modal
        open={showEditResponse}
        footer={null}
        closeIcon={null}
        closable={false}
        onCancel={handleEditResponseClose}
        width={500}
        destroyOnHidden
        modalRender={(modal) => {
          return React.cloneElement(modal as any, {
            style: { ...(modal as any).props.style, ...{ padding: 0, overflow: 'hidden' } },
          })
        }}
      >
        <EditResponseComment
          status={selectedStatus || 'ACCEPTED'}
          onSubmit={handleEditResponseSubmit}
          onCancel={handleEditResponseClose}
        />
      </Modal>
      <Modal
        open={showAttendeeList}
        footer={null}
        onCancel={handleAttendeeListClose}
        width={400}
        destroyOnHidden
        closable={false}
        modalRender={(modal) => {
          return React.cloneElement(modal as any, {
            style: { ...(modal as any).props.style, ...{ padding: 0, overflow: 'hidden' } },
          })
        }}
      >
        <AttendeeList instance={event} onClose={handleAttendeeListClose} />
      </Modal>
      <Modal
        wrapClassName={classNames('noteDetailModal')}
        className={classNames('mobile-fullpage')}
        open={showNoteDetail}
        footer={null}
        onCancel={() => setShowNoteDetail(false)}
        width={500}
        destroyOnHidden
        closable={false}
        modalRender={(modal) => {
          return React.cloneElement(modal as any, {
            style: { ...(modal as any).props.style, ...{ padding: 0, overflow: 'hidden' } },
          })
        }}
      >
        <NoteDetail note={event.description || ''} onClose={() => setShowNoteDetail(false)} />
      </Modal>
    </div>
  )
}

export default EventDetail
